import { Box, Divider, List, ListItemButton, ListItemIcon, ListItemText, Paper, Slide, Typography, styled, useTheme } from '@mui/material';
import { ModuleIcon } from './ModuleIcon';
import { ModuleIconKey } from './iconRegistry';
import { layoutTokens } from './layoutTokens';
import { radii } from '../../themes/tokens';

export interface ISubmenuItem {
  icon: ModuleIconKey;
  label: string;
  descricao?: string;
  path: string;
  active: boolean;
}

interface ISubmenuPanelProps {
  open: boolean;
  left: number;
  title?: string;
  items: ISubmenuItem[];
  onItemClick: (path: string) => void;
}

// Painel flutuante de submenu (desliza da lateral) — segue a paleta escura
// própria da sidebar (guia TORQ seção 8), como uma extensão visual dela, não
// da área de conteúdo. `theme.torq.sidebar.submenu*` já resolve pra cada
// variante de tema (inclusive a sidebar clara, ver Light.ts).
const SubmenuItemButton = styled(ListItemButton)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.25, 1.5),
  alignItems: 'flex-start',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  color: theme.torq.sidebar.submenuTextPrimary,
  transition: 'background-color 150ms ease, color 150ms ease',
  '&:hover': {
    backgroundColor: theme.torq.sidebar.submenuHover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.torq.sidebar.submenuSelectedBg,
    color: theme.torq.sidebar.textPrimary,
    '&:hover': {
      backgroundColor: theme.torq.sidebar.submenuSelectedBg,
    },
    // Barra de acento na borda esquerda do item selecionado, mesmo padrão
    // do item ativo na sidebar.
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '20%',
      bottom: '20%',
      width: '3px',
      borderRadius: '0 3px 3px 0',
      backgroundColor: theme.torq.sidebar.submenuIconSelected,
    },
  },
}));

// Margem entre o painel flutuante e a borda da tela — é o que dá a sensação
// de "flutuante" em vez de colado nas bordas.
const FLOAT_GAP = 10;

export const SubmenuPanel: React.FC<ISubmenuPanelProps> = ({ open, left, title, items, onItemClick }) => {
  const theme = useTheme();

  return (
    // Centraliza verticalmente com flexbox, não com `transform: translateY`
    // no Paper — o <Slide> já usa `transform` pra animar a entrada/saída, e
    // as duas transforms na mesma tag brigavam entre si: o `translateY`
    // de centralização era perdido, o painel ficava com o topo grudado no
    // meio da tela e o resto sobrando pra baixo, estourando a tela (dava
    // exatamente o scroll/corte que você viu). Um wrapper só pra
    // centralizar, sem transform nenhum, resolve.
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: `${left + FLOAT_GAP}px`,
        height: '100vh',
        width: layoutTokens.submenuWidth,
        display: 'flex',
        alignItems: 'center',
        zIndex: theme.zIndex.drawer + 2,
        pointerEvents: 'none',
      }}
    >
      <Slide direction="right" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={0}
        sx={{
          pointerEvents: 'auto',
          maxHeight: `calc(100vh - ${FLOAT_GAP * 2}px)`,
          width: layoutTokens.submenuWidth,
          borderRadius: `${radii.lg}px`,
          border: `1px solid ${theme.torq.sidebar.border}`,
          boxShadow: theme.torq.dialogShadow,
          backgroundColor: theme.torq.sidebar.submenuBg,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {title && (
          <>
            <Box sx={{ px: 3, py: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color={theme.torq.sidebar.textPrimary}>
                {title}
              </Typography>
            </Box>
            <Divider sx={{ borderColor: theme.torq.sidebar.border }} />
          </>
        )}

        <List sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
          {items.map((item) => (
            <SubmenuItemButton
              key={item.path}
              selected={item.active}
              onClick={() => onItemClick(item.path)}
            >
              <ListItemIcon sx={{ minWidth: 36, mt: '2px' }}>
                <ModuleIcon
                  name={item.icon}
                  sx={{
                    fontSize: '1.2rem',
                    color: item.active ? theme.torq.sidebar.submenuIconSelected : theme.torq.sidebar.submenuIconDefault,
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                secondary={item.descricao}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600, sx: { color: 'inherit' } }}
                secondaryTypographyProps={{ variant: 'caption', sx: { color: theme.torq.sidebar.textSecondary } }}
              />
            </SubmenuItemButton>
          ))}
        </List>
      </Paper>
      </Slide>
    </Box>
  );
};
